import { FieldWrapper } from '@design-system';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { StripeElementStyle } from '@stripe/stripe-js';
import { useTranslation } from 'next-i18next';
import { useRef, useState } from 'react';

import { CardBrand } from '../../../../components/Payments/CardBrand';
import { DetailsCardState } from './DetailsCardFields.state';

const CARD_ELEMENT_STYLE: StripeElementStyle = {
  base: {
    color: 'rgb(29, 22, 17)',
    fontFamily: '"Rima Avant Arte", "Arial", "sans-serif"',
    fontSize: '16px',
    fontWeight: '400',
    lineHeight: '20px',
    '::placeholder': {
      color: 'rgb(139, 121, 101)',
    },
  },
  invalid: {
    color: 'rgb(255, 82, 7)',
  },
};

const BRAND_STRIP = ['visa', 'mastercard', 'amex', 'discover'];

export type DetailsCardFieldsProps = {
  onChange: (state: DetailsCardState) => void;
};

export const DetailsCardFields = ({ onChange }: DetailsCardFieldsProps) => {
  const { t } = useTranslation('checkout');
  const [brand, setBrand] = useState<string | null>(null);
  const completionRef = useRef({ cvc: false, expiry: false, number: false });

  const emitChange = (
    nextBrand: string | null,
    nextCompletion: { cvc: boolean; expiry: boolean; number: boolean },
  ) => {
    onChange({
      brand: nextBrand,
      complete:
        nextCompletion.number && nextCompletion.expiry && nextCompletion.cvc,
    });
  };

  const knownBrand = brand && BRAND_STRIP.includes(brand) ? brand : null;

  return (
    <div className="flex flex-col gap-3">
      <FieldWrapper
        id="details-card-number"
        label={t('details.paymentCard.cardNumber')}
      >
        <div className="w-full flex-1 py-3">
          <CardNumberElement
            id="details-card-number"
            onChange={(event) => {
              const nextBrand = event.brand === 'unknown' ? null : event.brand;
              const nextCompletion = {
                ...completionRef.current,
                number: event.complete,
              };
              completionRef.current = nextCompletion;
              setBrand(nextBrand);
              emitChange(nextBrand, nextCompletion);
            }}
            options={{ style: CARD_ELEMENT_STYLE }}
          />
        </div>
        <span>
          <span className="flex flex-row items-center gap-1">
            {knownBrand ? (
              <CardBrand brand={knownBrand} />
            ) : (
              BRAND_STRIP.map((stripBrand) => (
                <CardBrand key={stripBrand} brand={stripBrand} />
              ))
            )}
          </span>
        </span>
      </FieldWrapper>

      <div className="grid grid-cols-2 gap-3">
        <FieldWrapper
          id="details-card-expiry"
          label={t('details.paymentCard.expiration')}
        >
          <div className="w-full flex-1 py-3">
            <CardExpiryElement
              id="details-card-expiry"
              onChange={(event) => {
                const nextCompletion = {
                  ...completionRef.current,
                  expiry: event.complete,
                };
                completionRef.current = nextCompletion;
                emitChange(brand, nextCompletion);
              }}
              options={{ style: CARD_ELEMENT_STYLE }}
            />
          </div>
        </FieldWrapper>

        <FieldWrapper
          id="details-card-cvc"
          label={t('details.paymentCard.securityCode')}
        >
          <div className="w-full flex-1 py-3">
            <CardCvcElement
              id="details-card-cvc"
              onChange={(event) => {
                const nextCompletion = {
                  ...completionRef.current,
                  cvc: event.complete,
                };
                completionRef.current = nextCompletion;
                emitChange(brand, nextCompletion);
              }}
              options={{ style: CARD_ELEMENT_STYLE }}
            />
          </div>
        </FieldWrapper>
      </div>
    </div>
  );
};
